import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError } from "axios";

import { DEFAULT_EXPORT_PAGINATION } from "../configs/pagination";
import { POST_BILLING_LOCATIONS } from "../middleware/routes";
import { RootState } from "../store";
import { BillingLocationsTableType, IBillingLocation, IBillingLocationsState } from "../types/billingLocations";
import { BillingPaymentScheduleType } from "../types/billings";
import { IFilterItem, IFilters } from "../types/filters";
import { IOrdering, IPagination } from "../types/pagination";
import { ISearch } from "../types/search";
import { Status } from "../types/state";
import { axiosRequestApi } from "../utils/axiosRequest";
import { downloadFile } from "../utils/download";
import { formatBillingLocationsForXlsx } from "../utils/formatData";
import { getSkeletonMockedArray } from "../utils/tables/getSkeletonMockedArray";
import { getSkeletonSuccessArray } from "../utils/tables/getSkeletonSuccessArray";
import { createXlsxFile } from "../utils/xlsx";
import { adaptRequestBodyForBackend, adaptResponseForFrontend } from "./utils/billingLocationsAdapt";
import { filterOutUndefinedFilters } from "./utils/filters";

export const defaultPagination = {
  page: 1,
  limit: 50,
};

export const defaultOrdering = {
  orderBy: "name",
  descending: false,
};

export const defaultSearch = {
  fulltext: "",
};

const defaultBillingLocation: IBillingLocation = {
  id: 0,
  locationId: 0,
  amount: 0,
  netAmount: 0,
  increaseComparison: 0,
  householdMembersCount: 0,
  name: "",
  enableTime: "",
  disableTime: "",
  previousWaste: 0,
  isPaid: false,
  isActive: true,
  realAmount: 0,
  currentWaste: 0,
  balance: 0,
  wasteIncrease: 0,
  paymentType: BillingPaymentScheduleType.Annual,
};

const defaultBillingLocations: IBillingLocation[] = getSkeletonMockedArray(defaultBillingLocation, 50, 0);

const defaultFilters: IFilters = {
  items: [],
  linkOperator: "and",
};

const initBillingLocationsState: IBillingLocationsState = {
  requestId: "",
  status: Status.idle,
  errorMsg: null,
  list: defaultBillingLocations,
  billingId: null,
  isNextPageEnable: false,
  pagination: defaultPagination,
  ordering: defaultOrdering,
  filters: defaultFilters,
  search: defaultSearch,
  scrollTop: 0,
};

export const billingLocationsSlice = createSlice({
  name: "billingLocations",
  initialState: initBillingLocationsState,
  reducers: {
    cleanUpBillingLocationsList: (state: IBillingLocationsState) => {
      state.status = Status.success;
      state.errorMsg = null;
      state.list = defaultBillingLocations;
      state.isNextPageEnable = false;
    },
    replaceBillingLocationsList: (state: IBillingLocationsState, action: PayloadAction<IBillingLocation>) => {
      const billingLocations = [...state.list];
      const newBillingLocations = billingLocations.map((billingLocation) => {
        if (billingLocation.id === action.payload.id) {
          return action.payload;
        }
        return billingLocation;
      });

      state.list = newBillingLocations;
    },
    setBillingLocationsPagination: (state: IBillingLocationsState, action: PayloadAction<IPagination>) => {
      state.pagination = action.payload;
    },
    setBillingLocationsOrdering: (state: IBillingLocationsState, action: PayloadAction<IOrdering>) => {
      state.ordering = action.payload;
    },
    setBillingLocationsFilter: (state, action: PayloadAction<IFilterItem>) => {
      const findIdx = state.filters.items.findIndex((item) => item.id === action.payload.id);
      if (findIdx === -1) {
        state.filters.items.push(action.payload);
        return;
      }

      state.filters.items[findIdx] = action.payload;
      state.filters.items = filterOutUndefinedFilters(state.filters.items, action.payload);
    },
    removeBillingLocationsFilter: (state, action: PayloadAction<string>) => {
      state.filters.items = state.filters.items.filter((item) => !item.id?.toString().startsWith(action.payload));
    },
    setBillingLocationsSearch: (state: IBillingLocationsState, action: PayloadAction<ISearch>) => {
      state.search = action.payload;
    },
    setBillingLocationsScrollTop: (state: IBillingLocationsState, action: PayloadAction<number>) => {
      state.scrollTop = action.payload;
    },
    resetBillingLocationsStateIfBillingIdChanged: (
      state: IBillingLocationsState,
      action: PayloadAction<IBillingLocationsState["billingId"]>
    ) => {
      if (state.billingId === action.payload) {
        return;
      }
      return initBillingLocationsState;
    },
    resetBillingLocationsState: () => {
      return initBillingLocationsState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBillingLocationsThunk.pending, (state, action) => {
        state.status = Status.requesting;
        state.requestId = action.meta.requestId;

        if (state.pagination.page > 1) {
          const mockedBillingLocations = getSkeletonMockedArray(defaultBillingLocations, 3, state.list.length);
          state.list = [...state.list, ...mockedBillingLocations];
        }
      })
      .addCase(getBillingLocationsThunk.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) {
          return;
        }

        const locationsList = action.payload.list as IBillingLocation[];

        const billingLocationsFromState: IBillingLocation[] = state.list;
        const billingLocationsFromResponse: IBillingLocation[] = locationsList;
        const newBillingLocations = getSkeletonSuccessArray(billingLocationsFromState, billingLocationsFromResponse);

        state.billingId = action.payload.billingId || null;
        state.list = newBillingLocations;
        state.isNextPageEnable = locationsList.length >= state.pagination.limit;
        state.errorMsg = null;
        state.status = Status.success;
      })
      .addCase(getBillingLocationsThunk.rejected, (state) => {
        state.status = Status.error;
        state.errorMsg = "there has been an error";
      });
  },
});

export const getBillingLocationsThunk = createAsyncThunk<any, number | undefined, { state: RootState }>(
  "billingLocations/getBillingLocationsThunk",
  async (billingIdProp, { getState }) => {
    const state = getState();
    const stateBillingId = state.billing.billing.billingId;
    const billingId = billingIdProp || stateBillingId;

    const requestObject = adaptRequestBodyForBackend(state.billingLocations);
    const response = await axiosRequestApi.post(`${POST_BILLING_LOCATIONS}/${billingId}/locations`, requestObject);

    return {
      billingId,
      list: adaptResponseForFrontend(response.data),
    };
  }
);

export const exportBillingLocationsThunk = createAsyncThunk<
  any,
  { billingId?: number; tableType: BillingLocationsTableType },
  { state: RootState }
>("billingLocations/exportBillingLocationsThunk", async ({ billingId, tableType }, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const stateBillingId = state.billing.billing.billingId;

    const requestObject = adaptRequestBodyForBackend(state.billingLocations);
    requestObject.pagination = DEFAULT_EXPORT_PAGINATION;

    const response = await axiosRequestApi.post(
      `${POST_BILLING_LOCATIONS}/${billingId || stateBillingId}/locations`,
      requestObject
    );

    const billingLocations = adaptResponseForFrontend(response.data);
    const formattedData = formatBillingLocationsForXlsx(billingLocations, tableType);
    const file = createXlsxFile(formattedData);
    downloadFile(file, `billing-locations-${state.billing.billing.year}.xlsx`);

    return true;
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    const message = axiosError?.response?.data?.message;
    return rejectWithValue(message);
  }
});

export const {
  cleanUpBillingLocationsList,
  setBillingLocationsPagination,
  setBillingLocationsOrdering,
  setBillingLocationsSearch,
  setBillingLocationsScrollTop,
  replaceBillingLocationsList,
  resetBillingLocationsState,
  resetBillingLocationsStateIfBillingIdChanged,
  setBillingLocationsFilter,
  removeBillingLocationsFilter,
} = billingLocationsSlice.actions;

export const billingLocationsReducer = billingLocationsSlice.reducer;
